import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { subscribeWithSelector } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { Modal, message } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import sqliteStorage from "./sqliteStorage";
import { trace } from "../utils/debugTrace";
import { advanceOrderFloorViolation } from "../utils/advanceOrderValidation";

// BUG-2020 (global floor check, 2026-09-01): every discount/edit path that
// commits a cart change -- however many scattered call sites, however
// mismatched their own internal argument-passing might be -- has to go
// through this one setCart action to actually take effect. Checking the
// floor HERE instead of at each individual call site (discount functions,
// addProductToCart, etc.) means it cannot be bypassed or missed by a call
// site nobody's audited yet. advanceOrderFloorViolation itself is a no-op
// for every cart that isn't a retrieved, paid Advance/Layaway order (see its
// own isAdvanceOrderWithPayment gate), so this adds no overhead/behavior
// change to any other setCart call in the app. setExchangeCart is a
// separate action entirely, so Exchange edits never reach this check.
// A single logical user action (e.g. Apply Discount) can drive MULTIPLE setCart
// calls in a row -- the pricing-rule function's own internal commit, then the
// caller's later commit of its fully recomputed totals. Both would violate the
// same floor and both would reject, showing two stacked modals for one click.
// Suppress a re-show within this window instead of nagging twice for the same
// underlying rejection -- the cart is already correctly protected either way.
const FLOOR_MODAL_DEDUPE_MS = 800;
let lastFloorModalShownAt = 0;
const showFloorViolationModal = (floorViolation) => {
  const now = Date.now();
  if (now - lastFloorModalShownAt < FLOOR_MODAL_DEDUPE_MS) return;
  lastFloorModalShownAt = now;
  message.destroy(); // clear any "applied successfully" toast the caller already queued before this rejection
  Modal.warning({
    title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>Amount Cannot Be Reduced Below Paid Amount</div>,
    content: (
      <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
        <p style={{ marginBottom: 0, color: "#0F0718", fontSize: "1em" }}>{floorViolation}</p>
      </div>
    ),
    icon: <WarningOutlined />,
    okText: "Ok",
    okButtonProps: { style: { display: "inline-block" } },
    modalRender: (node) => <div className="custom-confirm-modal-wrapper">{node}</div>,
  });
};

const generateId = () => uuidv4().replace(/-/g, "").toUpperCase();

const EMPTY_CART = {
  items: [],
  couponInput: [],
  advancePayment: 0,
  total: 0,
  tax: 0,
  discount: 0,
  paid: 0,
  change: 0,
  totalQty: 0,
  roundOff: 0,
  payments: [],
  redemptionPoints: 0,
  accumulationPoints: 0,
  creditAmount: 0,
  docType: "Invoice",
  sOrderID: "",
  referenceId: "",
  giftCardRefId: "",
  couponRefId: "",
  customer: null,
  salesRepId: null,
  cardPaymentData: {},
  documentno: "",
  userName: "",
  tillName: "",
  subTotal: 0,
  itemsSummery: {},
  amountInWords: "",
  groupedItemsByTaxRate: {},
  serviceCharge: 0,
  tip: 0,
  deiveryCharge: 0,
  packingCharge: 0,
  others: 0,
  refundAmount: 0,
  totalStd: 0,
  layAway: "N",
};

const useCartStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // State
        cart: { ...EMPTY_CART },
        selectedProductInCart: {},
        exchangeCart: null,
        _hydrated: false,

        // Actions — setCart supports functional updater like useState: setCart(prev => ({...prev, field}))
        // Always merges with EMPTY_CART to ensure array/object fields (payments, items, etc.) are never undefined.
        // Returns true if the write was applied, false if it was rejected by the floor
        // check below -- callers that show their own "applied"/"success" feedback after
        // calling setCart should check this return value first (see processManualDiscount,
        // useCartOperations.js) rather than assuming the write always lands.
        setCart: (cartOrFn) => {
          const prevLen = get().cart?.items?.length || 0;
          const nextCart = typeof cartOrFn === "function" ? { ...EMPTY_CART, ...cartOrFn(get().cart) } : { ...EMPTY_CART, ...cartOrFn };
          // BUG-2020 (global floor check): reject the write outright and leave the
          // cart unchanged -- the caller (a discount function, addProductToCart,
          // whatever) already computed and is trying to commit nextCart; there is
          // nothing to "revert" here since the store's current state IS the
          // pre-violation state until this set() actually runs.
          const floorViolation = advanceOrderFloorViolation(nextCart, nextCart.total);
          if (floorViolation) {
            showFloorViolationModal(floorViolation);
            return false;
          }
          set({ cart: nextCart });
          const nextLen = get().cart?.items?.length || 0;
          if (prevLen !== nextLen || nextLen === 0) {
            trace("CartDiag", "setCart", { prevLen, nextLen, sOrderID: get().cart?.sOrderID, docno: get().cart?.documentno, stack: new Error().stack?.split("\n").slice(1, 5).join(" | ") });
          }
          return true;
        },

        setSelectedProductInCart: (product) => set({ selectedProductInCart: product }),

        setExchangeCart: (cartOrFn) => {
          if (typeof cartOrFn === "function") {
            set((state) => ({ exchangeCart: cartOrFn(state.exchangeCart) }));
          } else {
            set({ exchangeCart: cartOrFn });
          }
        },

        addItem: (item) =>
          set((state) => {
            const items = [...state.cart.items, item];
            const total = items.reduce((sum, i) => sum + (i.nettotal || 0), 0);
            const totalQty = items.reduce((sum, i) => sum + Math.abs(i.weight || 0), 0);
            const tax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0);
            return {
              cart: { ...state.cart, items, total, totalQty, tax },
            };
          }),

        removeItem: (lineId) =>
          set((state) => {
            const items = state.cart.items.filter((i) => i.lineId !== lineId);
            const total = items.reduce((sum, i) => sum + (i.nettotal || 0), 0);
            const totalQty = items.reduce((sum, i) => sum + Math.abs(i.weight || 0), 0);
            const tax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0);
            return {
              cart: { ...state.cart, items, total, totalQty, tax },
              selectedProductInCart: {},
            };
          }),

        updateItemQty: (lineId, newQty) =>
          set((state) => {
            const items = state.cart.items.map((item) => {
              if (item.lineId !== lineId) return item;
              const weight = newQty;
              const nettotal = item.salePrice * weight;
              const taxAmount =
                item.taxRate > 0
                  ? item.taxFlag === "Y"
                    ? (nettotal * item.taxRate) / 100
                    : nettotal - nettotal / (1 + item.taxRate / 100)
                  : 0;
              return { ...item, weight, nettotal, total: nettotal, taxAmount };
            });
            const total = items.reduce((sum, i) => sum + (i.nettotal || 0), 0);
            const totalQty = items.reduce((sum, i) => sum + Math.abs(i.weight || 0), 0);
            const tax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0);
            return { cart: { ...state.cart, items, total, totalQty, tax } };
          }),

        clearCart: (preserveFields = {}) => {
          trace("CartDiag", "clearCart", { prevLen: get().cart?.items?.length || 0, stack: new Error().stack?.split("\n").slice(1, 5).join(" | ") });
          set({
            cart: {
              ...EMPTY_CART,
              sOrderID: generateId(),
              referenceId: generateId(),
              giftCardRefId: generateId(),
              couponRefId: generateId(),
              ...preserveFields,
            },
            selectedProductInCart: {},
          });
        },

        // Derived selectors
        getTotal: () => get().cart.total || 0,
        getItemCount: () => get().cart.items?.length || 0,
        getTotalQty: () => get().cart.totalQty || 0,
      }),
      {
        name: "cartObj",
        storage: createJSONStorage(() => sqliteStorage),
        skipHydration: true,
        partialize: (state) => ({ cart: state.cart, exchangeCart: state.exchangeCart }),
        merge: (persisted, current) => {
          const cart = persisted?.cart ? { ...EMPTY_CART, ...persisted.cart } : current.cart;
          // Stale identity guard: sqliteStorage debounces writes by 100ms, so a
          // reload can restore a pre-checkout snapshot whose sOrderID was already
          // consumed by a saved order. Reusing it overwrites that order locally
          // (db.orders is keyed on sOrderID) and gets silently deduped by the
          // backend, losing the new bill server-side. An empty cart has nothing
          // bound to its identity, so always reissue; non-empty carts are checked
          // against db.orders on POS mount (merge is sync — it cannot query).
          if (!cart.items?.length && !cart.payments?.length) {
            cart.sOrderID = generateId();
            cart.referenceId = generateId();
            cart.giftCardRefId = generateId();
            cart.couponRefId = generateId();
            // BUG-2072: an empty cart's documentno preview can carry a prior store's
            // till prefix across an Unlink Till + store switch (till.js re-hydrates this
            // store on every /till mount, but nothing else invalidates the stale preview).
            // Clearing it here forces Core/index.js's mount guard to regenerate it from
            // the newly selected store's tillData.prefix instead of reusing the stale one.
            cart.documentno = "";
          }
          // Ensure cart always has valid IDs (first open / web mode starts with empty IDs)
          if (!cart.sOrderID) cart.sOrderID = generateId();
          if (!cart.referenceId) cart.referenceId = generateId();
          if (!cart.giftCardRefId) cart.giftCardRefId = generateId();
          if (!cart.couponRefId) cart.couponRefId = generateId();
          // documentno is set by Core/index.js on POS mount (needs tillData + documentSequence)
          return {
            ...current,
            cart,
            exchangeCart: persisted?.exchangeCart ?? current.exchangeCart,
            _hydrated: true,
          };
        },
      }
    )
  )
);

// BUG-2066/2067/2068 diagnostics: same purpose as useSessionStore's
// StoreDiag.session — find what re-renders PrivateRoute's ancestors in the gap
// between handleLogin clearing cookies and window.location.assign() landing.
// Cart updates are frequent enough during a live session to be a plausible
// trigger independent of the lock flow itself. Logging only changed key names
// (not full before/after cart contents, which can be large) to keep entries
// small. Remove once root cause is confirmed.
useCartStore.subscribe((state, prevState) => {
  try {
    const changedKeys = Object.keys(state).filter((key) => typeof state[key] !== "function" && state[key] !== prevState[key]);
    if (changedKeys.length > 0) {
      trace("StoreDiag.cart", "useCartStore changed", { changedKeys, itemCount: state.cart?.items?.length });
    }
  } catch {
    // never let diagnostic instrumentation break the store
  }
});

export { EMPTY_CART };
export default useCartStore;
